/**
 * Catálogo de eventos disparadores de campañas.
 *
 * Cada evento tiene:
 *  - key: identificador único (lo que se persiste en EventTrigger.eventKey)
 *  - label: nombre humano
 *  - emoji: icono
 *  - category: grupo en el picker UI
 *  - description: explicación corta
 *  - params: parámetros configurables (ej. "horas desde X", "monto mínimo")
 *
 * En producción esto vendría del backend (event-tracking service).
 * Acá lo hardcodeamos para que la UI tenga catálogo rico desde el día 1.
 */

export type EventCategory =
  | "lifecycle"
  | "behavioral"
  | "catalog"
  | "whatsapp"; // conversacionales internos al canal

export interface EventParamSpec {
  key: string;
  label: string;
  /** Tipo de input: number (con sufijo "h", "días", "$") o text */
  type: "number" | "text" | "select";
  /** Sufijo visual del input ("hs", "días", "$"…) */
  suffix?: string;
  /** Solo para type=select */
  options?: { value: string | number; label: string }[];
  /** Valor por defecto al crear */
  defaultValue?: string | number;
  /** Si lo pide o es opcional */
  required?: boolean;
}

export interface EventDefinition {
  key: string;
  label: string;
  emoji: string;
  category: EventCategory;
  description: string;
  /** Sub-categoría opcional para agrupar visualmente (ej. "compras", "engagement") */
  subgroup?: string;
  params?: EventParamSpec[];
}

export const EVENT_CATALOG: EventDefinition[] = [
  /* ─────────── LIFECYCLE (8) ─────────── */
  {
    key: "signup",
    label: "Cliente nuevo creó su cuenta",
    emoji: "👋",
    category: "lifecycle",
    description: "Dispara apenas el usuario completa el registro.",
  },
  {
    key: "first_purchase",
    label: "Primera compra completada",
    emoji: "🎉",
    category: "lifecycle",
    description: "Después de que el cliente cierra su primer pedido pago.",
  },
  {
    key: "birthday",
    label: "Cumpleaños del cliente",
    emoji: "🎂",
    category: "lifecycle",
    description:
      "Se dispara el día del cumple según la fecha de nacimiento del CRM.",
  },
  {
    key: "customer_anniversary",
    label: "Aniversario como cliente",
    emoji: "🥂",
    category: "lifecycle",
    description: "1 año desde su primera compra.",
  },
  {
    key: "inactive_days",
    label: "Inactividad sin pedir",
    emoji: "😴",
    category: "lifecycle",
    description: "Dispara si pasan X días desde el último pedido.",
    params: [
      {
        key: "days",
        label: "Días sin pedir",
        type: "number",
        suffix: "días",
        defaultValue: 60,
        required: true,
      },
    ],
  },
  {
    key: "milestone_orders",
    label: "Milestone de pedidos",
    emoji: "🏆",
    category: "lifecycle",
    description: "Llegó a su pedido número X.",
    params: [
      {
        key: "count",
        label: "Cantidad de pedidos",
        type: "number",
        defaultValue: 10,
        required: true,
      },
    ],
  },
  {
    key: "vip_upgrade",
    label: "Subió a VIP",
    emoji: "👑",
    category: "lifecycle",
    description: "Entró al top 20% de LTV.",
  },
  {
    key: "churn_risk",
    label: "Riesgo de churn detectado",
    emoji: "⚠️",
    category: "lifecycle",
    description:
      "La IA detecta caída de frecuencia + reducción de ticket promedio.",
  },

  /* ─────────── BEHAVIORAL · COMPRAS (8) ─────────── */
  {
    key: "cart_abandoned",
    label: "Carrito abandonado",
    emoji: "🛒",
    category: "behavioral",
    subgroup: "compras",
    description: "Armó carrito y no completó el pedido.",
    params: [
      {
        key: "hours",
        label: "Horas desde abandono",
        type: "number",
        suffix: "hs",
        defaultValue: 2,
        required: true,
      },
      {
        key: "minValue",
        label: "Valor mínimo del carrito",
        type: "number",
        suffix: "$",
        defaultValue: 1500,
      },
    ],
  },
  {
    key: "low_ticket",
    label: "Ticket promedio bajó",
    emoji: "📉",
    category: "behavioral",
    subgroup: "compras",
    description:
      "Su ticket promedio cayó debajo del promedio de la marca por X% en los últimos N pedidos.",
    params: [
      {
        key: "percentBelow",
        label: "% debajo del promedio",
        type: "number",
        suffix: "%",
        defaultValue: 20,
      },
    ],
  },
  {
    key: "frequency_drop",
    label: "Frecuencia de compra cayó",
    emoji: "📊",
    category: "behavioral",
    subgroup: "compras",
    description:
      "Pedía cada X días, ahora pasaron más del doble. Señal temprana de churn.",
  },
  {
    key: "payment_failed",
    label: "Pago rechazado",
    emoji: "💳",
    category: "behavioral",
    subgroup: "compras",
    description: "Intentó pagar y falló (tarjeta rechazada, fondos, etc.).",
  },
  {
    key: "refund_requested",
    label: "Pidió devolución",
    emoji: "↩️",
    category: "behavioral",
    subgroup: "compras",
    description: "Solicitó refund en uno de sus pedidos.",
  },
  {
    key: "high_intent_browse",
    label: "Intención alta de compra",
    emoji: "🔥",
    category: "behavioral",
    subgroup: "engagement",
    description:
      "Vio el mismo producto 3+ veces en 7 días pero no lo agregó al carrito.",
  },
  {
    key: "first_review",
    label: "Dejó primera review",
    emoji: "⭐",
    category: "behavioral",
    subgroup: "engagement",
    description: "Completó una review/calificación post-pedido.",
  },
  {
    key: "loyalty_unlock",
    label: "Desbloqueó beneficio del programa",
    emoji: "🎁",
    category: "behavioral",
    subgroup: "engagement",
    description: "Llegó al threshold para el siguiente nivel de fidelización.",
  },

  /* ─────────── CATÁLOGO DE PRODUCTOS (6) ─────────── */
  {
    key: "back_in_stock",
    label: "Producto volvió al stock",
    emoji: "📦",
    category: "catalog",
    description:
      "Item que el cliente vio o pidió antes está disponible de nuevo.",
  },
  {
    key: "price_drop",
    label: "Baja de precio en producto deseado",
    emoji: "💸",
    category: "catalog",
    description:
      "Item que el cliente miró tiene ahora un precio menor que cuando lo vio.",
    params: [
      {
        key: "minPercent",
        label: "% mínimo de baja",
        type: "number",
        suffix: "%",
        defaultValue: 10,
      },
    ],
  },
  {
    key: "new_launch",
    label: "Nuevo producto lanzado",
    emoji: "✨",
    category: "catalog",
    description:
      "Se carga un producto nuevo que coincide con el perfil del cliente.",
  },
  {
    key: "viewed_not_bought",
    label: "Vio item pero no compró",
    emoji: "👀",
    category: "catalog",
    description: "Vio el mismo producto X veces sin pasar a checkout.",
    params: [
      {
        key: "minViews",
        label: "Cantidad mínima de views",
        type: "number",
        defaultValue: 3,
      },
      {
        key: "withinDays",
        label: "En los últimos N días",
        type: "number",
        suffix: "días",
        defaultValue: 7,
      },
    ],
  },
  {
    key: "wishlist_promo",
    label: "Item de wishlist en promo",
    emoji: "❤️",
    category: "catalog",
    description: "Algo de su wishlist entra en oferta.",
  },
  {
    key: "low_stock_alert",
    label: "Stock crítico de favorito",
    emoji: "🚨",
    category: "catalog",
    description: "Quedan pocas unidades de un item que el cliente compra recurrentemente.",
    params: [
      {
        key: "unitsLeft",
        label: "Unidades restantes ≤",
        type: "number",
        defaultValue: 5,
      },
    ],
  },

  /* ─────────── CONVERSACIONAL WHATSAPP (8) ─────────── */
  {
    key: "wa_inbound_received",
    label: "Mensaje WhatsApp recibido",
    emoji: "💬",
    category: "whatsapp",
    description:
      "Cualquier mensaje entrante. Usado generalmente con keywords o categoría.",
  },
  {
    key: "wa_keyword_match",
    label: "Keyword detectada",
    emoji: "🔍",
    category: "whatsapp",
    description:
      'Detecta palabras específicas en mensaje del usuario (ej. "menu", "cancelar").',
    params: [
      {
        key: "keywords",
        label: "Keywords (separadas por coma)",
        type: "text",
        defaultValue: "menu, carta",
        required: true,
      },
    ],
  },
  {
    key: "wa_no_response_24h",
    label: "Sin respuesta de la marca >24h",
    emoji: "⏰",
    category: "whatsapp",
    description:
      "Cliente escribió y la marca no respondió en 24h. Útil para recordatorio interno.",
  },
  {
    key: "wa_conversation_started",
    label: "Conversación iniciada",
    emoji: "💌",
    category: "whatsapp",
    description: "Primer mensaje del cliente en una nueva conversación.",
  },
  {
    key: "wa_link_clicked",
    label: "Clickeó link enviado",
    emoji: "🔗",
    category: "whatsapp",
    description: "Tracking de click en links enviados por WhatsApp.",
  },
  {
    key: "wa_template_responded",
    label: "Respondió a una plantilla",
    emoji: "↩️",
    category: "whatsapp",
    description: "Respondió a un mensaje enviado por una campaña anterior.",
  },
  {
    key: "wa_optout",
    label: "Cliente pidió no recibir más",
    emoji: "🚫",
    category: "whatsapp",
    description: "Mandó 'baja', 'stop' o equivalente. Importante para compliance.",
  },
  {
    key: "wa_reservation_intent",
    label: "Pidió hacer una reserva",
    emoji: "📅",
    category: "whatsapp",
    description: "La IA detecta intención de reserva en el mensaje entrante.",
  },
];

/* ─── Helpers ─── */

export const EVENTS_BY_CATEGORY: Record<EventCategory, EventDefinition[]> = {
  lifecycle: EVENT_CATALOG.filter((e) => e.category === "lifecycle"),
  behavioral: EVENT_CATALOG.filter((e) => e.category === "behavioral"),
  catalog: EVENT_CATALOG.filter((e) => e.category === "catalog"),
  whatsapp: EVENT_CATALOG.filter((e) => e.category === "whatsapp"),
};

export const EVENT_BY_KEY: Record<string, EventDefinition> = EVENT_CATALOG.reduce(
  (acc, e) => {
    acc[e.key] = e;
    return acc;
  },
  {} as Record<string, EventDefinition>,
);

export const CATEGORY_META: Record<
  EventCategory,
  { label: string; emoji: string; color: string }
> = {
  lifecycle: { label: "Lifecycle", emoji: "🌱", color: "emerald" },
  behavioral: { label: "Comportamiento de compra", emoji: "🛒", color: "blue" },
  catalog: { label: "Catálogo de productos", emoji: "📦", color: "amber" },
  whatsapp: { label: "Conversacional WhatsApp", emoji: "💬", color: "violet" },
};

/* ─── Categorías de mensajes WA ─── */

export const WA_MESSAGE_CATEGORIES: {
  key: string;
  label: string;
  emoji: string;
  description: string;
  defaultKeywords?: string[];
}[] = [
  {
    key: "complaint",
    label: "Queja / Reclamo",
    emoji: "😤",
    description: "El cliente expresa una queja por experiencia, producto o servicio.",
    defaultKeywords: ["queja", "reclamo", "horrible", "pésimo", "frío"],
  },
  {
    key: "inquiry",
    label: "Consulta general",
    emoji: "❓",
    description: "Pregunta sobre la marca, horarios, sucursales, productos.",
    defaultKeywords: ["consulta", "info", "información"],
  },
  {
    key: "cancellation",
    label: "Cancelación de pedido",
    emoji: "❌",
    description: "Pide cancelar un pedido o reserva.",
    defaultKeywords: ["cancelar", "anular", "stop"],
  },
  {
    key: "thanks",
    label: "Agradecimiento",
    emoji: "🙏",
    description: "Cliente agradece o felicita por algo positivo.",
    defaultKeywords: ["gracias", "excelente", "buenísimo"],
  },
  {
    key: "info_request",
    label: "Pide información puntual",
    emoji: "📋",
    description: "Pregunta sobre algo específico (precio, ingredientes, alergenos).",
  },
  {
    key: "menu_request",
    label: "Pide menú / carta",
    emoji: "📃",
    description: "Pregunta por opciones de menú, carta, sabores, combos.",
    defaultKeywords: ["menu", "menú", "carta", "qué tienen"],
  },
  {
    key: "reservation_request",
    label: "Pide reservar",
    emoji: "📅",
    description: "Quiere hacer una reserva para fecha/hora.",
    defaultKeywords: ["reserva", "reservar", "mesa para"],
  },
  {
    key: "other",
    label: "Otro / Sin categorizar",
    emoji: "💭",
    description: "Mensaje que no encaja en las categorías anteriores.",
  },
];
